// @ts-check

/**
 * @file Route: storelocator
 *
 * This file is compiled separately from the main js entryfile and it is
 * included inlined *only* in its template. In this way the scripts written in
 * this file will always remain scoped, helping to avoid unintended side effects
 * and reducing the requested shared bundle size. This file serves also as the
 * main entry point for the route `storelocator`, therefore not only
 * scripts but also styles specific to this route should be imported here.
 * (Don't remove this comment, all comments will be stripped out in production.)
 */

import "layouts/main";
import { $, on, getDataAttr, setDataAttr, $all, forEach } from "@olmokit/dom";
import "@olmokit/core/forms/select/material";
import ajax from "@olmokit/core/ajax";
import scrollTo from "@olmokit/core/scroll/scrollTo";
import {
    navigateToMergedParams,
    navigateWithoutUrlParam
  } from "@olmokit/browser";
import {
    getUrlQueryParams,
    removeDuplicatesByKey,
    debounce,
  } from "@olmokit/utils";
import { render } from "@olmokit/utils/render";
import loadResource from "@olmokit/core/ajax/loadResource";
import { breakpoints } from "@olmokit/core/scss";
import "@olmokit/core/progress/loading.scss";
import {
  GMAP_ASYNC_BUNDLE_KEY,
  GMAP_SCRIPT_URL,
  GMAP_USA_CENTER,
  GMAP_USA_BOUNDS
} from "config/index";
import { Searchaddress } from "components/Searchaddress";
import { dataLayerStore } from "components/Datalayer"
import mapStyle from "./mapstyle.json";
import { globalConf } from "@olmokit/core/data";
import "./index.scss";

/**
 * @typedef {Object} $response
 * @property {$response_Place[]} data
 *
 * @typedef {Object} $response_Place
 * @property {string} uuid Results of php `uniqid()`
 * @property {string} name E.g. "The Belknap White Group, Avon"
 * @property {number} lat E.g. "42.1434517"
 * @property {number} lng E.g. "-71.06045519999999"
 * @property {string} addr_1 E.g. "Street, number"
 * @property {string} addr_2 E.g. "City, State ZIP"
 * @property {string} markertype Flag
 *
 * @typedef {Object} $request
 * @property {string | number} lat E.g. "42.1434517"
 * @property {string | number} lng E.g. "-71.06045519999999"
 * @property {string | number} threshold E.g. "20"
 *
 * @typedef {Object} $ui_Place
 * @property {google.maps.Marker} marker The place's Google Maps marker instance
 * @property {number} distance Rounded actual value for UI
 * @property {number} _distance Actual value with decimals
 *
 * @typedef {$response_Place & $ui_Place} Locator_Place
 */

/**
 * Storelocator page wrapper function
 *
 * @param {string} dataEndpoint
 */
function locator(dataEndpoint) {
  const ATTR_PLACE_UUID = "place-uuid";

  /** @type {HTMLElement} */
  const $root = $(".locator:");

  /** @type {HTMLElement} */
  const $map = $(".locator:map__holder", $root);

  /** @type {HTMLDivElement} */
  const $distanceWrap = $(".locator:search__distance", $root);

  /** @type {HTMLDivElement} */
  const $distanceCustom = $(".locator:search__distanceCustom", $root);

  /** @type {HTMLSelectElement} */
  const $distance = $("#locator-distance", $root);

  /** @type {HTMLElement} */
  const $resultsScroller = $(".locator:results", $root);

  /** @type {HTMLElement} */
  const $results = $(".locator:results__list", $root);

  /**
   * All places available
   * @type {Locator_Place[]}
   */
  const placesList = [];

  /**
   * All places available mapped by uuid
   * @type {{ [key: string]: Locator_Place }}
   */
  const placesMap = {};

  /**
   * Places that are currently visible on the map
   * @type {Locator_Place[]}
   */
  let placesVisible = [];

  /**
   * Places that are currentyl visible in the results view
   * @type {Locator_Place[]}
   */
  let placesResults = [];

  /**
   * Render Google Maps InfoWindow content
   * @type {(place: Locator_Place) => string}
   */
  const renderInfoWindow = render($("#locator-infowindow").innerHTML);

  /**
   * Render Locator results
   * @type {(data: { results: Locator_Place[] }) => string}
   */
  const renderResults = render($("#locator-results").innerHTML);

  let searchaddressInstance;

  /**
   * Closured value of the last place opened (used to update the InfoWindow
   * dynamically)
   *
   * @type {string}
   * */
  let lastOpenedPlaceUiid;

  /**
   * Can start render result flag
   *
   * Results view does not always render immediately, for instance in these
   * cases:
   * - The user lands on the locator without a prefilled address or query. In
   * this case we wait for one of these events to trigger the render:
   *   - the user has a deeplinked found address
   *   - the user search and finds an address
   *   - the user clicks on a marker
   *   - the user zooms in and zoom is higher than 6
   * - We also render the results if the deelinked zoom is equal or higher than
   * 7, as it is quite a closeup...
   *
   * @type {boolean}
   * */
  let canStartRenderResults = false;

  /**
   * Flag to determine if the user has searched and found an address or if he
   * is freely navigating the map
   *
   * @type {boolean}
   * */
  let hasSearchedAddress;

  /**
   * The last meaningful center is either the one determined by the
   * `searchaddress` result (either geolocation or found address, both
   * autocompleted and manual), or the current map center when the user freely
   * navigates (panning/zooming) the gmapsMap.
   *
   * @type {Object} center
   * @property {number} center.lat Center lat
   * @property {number} center.lng Center lng
   * */
  let lastMapMeaningfulCenter;

  /**
   * Google Maps API
   * @type {google.maps}
   */
  let gmaps;

  /**
   * Google Maps Map instance
   * @type {google.maps.Map}
   */
  let gmapsMap;

  /**
   * Google Maps Map instance
   * @type {google.maps.places.PlacesService}
   */
  let gmapsPlaces;

  /**
   * Google Maps Circle instance to use to calculate the areas to show based
   * on the given distance
   * @type {google.maps.Circle}
   */
  let gmapsCircle;

  /**
   * Google Maps InfoWindow instance, it is reused for all InfoWindows
   * @type {google.maps.InfoWindow}
   */
  let gmapsInfoWindow;

  /**
   * Init
   *
   * Wait to have both the data and the Google Maps aPI available then create
   * all places. Initialise also the searchaddress autocomplete with callbacks.
   */
  function init() {
    const promiseData = getRemoteData();
    const promiseApi = loadGmaps();
    const deep = getDeeplinkedState();
    const hasDeeplinkedFullAddress = deep.address && deep.lat && deep.lng;
    const hadDeeplinkedFreeQuery = deep.query;
    hasSearchedAddress = !!deep.address || !!deep.query;

    loadingStart();

    // if there is no deep address or query disable the distance select
    if (!hasSearchedAddress) {
      setDistanceStatus(false);

      if (deep.zoom >= 7) {
        canStartRenderResults = true;
      }
    } else {
      canStartRenderResults = true;
    }

    // prefill deep distance select value
    if (deep.distance) {
      $distance.value = deep.distance + "";
    }

    // load all async data
    Promise.all([promiseData, promiseApi]).then(([response]) => {
      initGmaps(
        deep.posLat || deep.lat,
        deep.posLng || deep.lng,
        deep.zoom || (hasDeeplinkedFullAddress ? 7 : 5)
      );
      createMapPlaces(response.data);
      bindResultsClick();
      listenGmapsEvents();
      loadingDone();

      // three scenarios now:
      // 1: we arrive here with a deep full address and coords
      if (hasDeeplinkedFullAddress) {
        handleFoundAddress(deep);
        const bounds = handleFoundAddress(deep);
        if (!deep.posLat && !deep.posLng) {
          centerMapOnAddressBounds(bounds);
        }
      }
      // 2: we arrive here with a free search query
      else if (hadDeeplinkedFreeQuery) {
        handleFreeQuerySearch(deep.query);
      }
      // 3: we arrive here with nothing much
      else {
        setFreeMapMeaningfulCenter();
      }
    });

    // initialise searchaddress component
    searchaddressInstance = Searchaddress($root, {
      onCoords: coords => {
        const bounds = handleFoundAddress(coords);
        centerMapOnAddressBounds(bounds);
      },
      onEmpty: () => {
        setDistanceStatus(false);
        hasSearchedAddress = false;
        gmapsCircle.setVisible(false);
        setFreeMapMeaningfulCenter();
        const { address, query, lat, lng, ...otherParams } = getUrlQueryParams();
        navigateToMergedParams(otherParams);
      },
      onSubmit: query => handleFreeQuerySearch(query)
    }).init();

    // bind distance select events
    on($distanceWrap, "mousedown", handleClickDistance);
    on($distance, "change", handleChangeDistance);
  }

  /**
   * Load google maps script asynchrnously
   */
  function loadGmaps() {
    return loadResource([GMAP_SCRIPT_URL], GMAP_ASYNC_BUNDLE_KEY, {
      numRetries: 3,
      returnPromise: true
    });
  }

  /**
   * Init google maps API
   *
   * Read deeplinked lat, lng and zoom to deeplink map status.
   * Binds map listeners (note that `bounds_changed` is called both on user
   * panning/zooming action and on resize of the viewport).
   *
   * Use https://www.latlngg.net/ to calculate an approximate default center
   * For a list of google maps events
   * @see https://developers.google.com/maps/documentation/javascript/events
   *
   * @param {number} centerLat
   * @param {number} centerLng
   * @param {number} zoom
   */
  function initGmaps(centerLat, centerLng, zoom) {
    gmaps = window["google"].maps;
    const center = {
      lat: centerLat || GMAP_USA_CENTER.lat,
      lng: centerLng || GMAP_USA_CENTER.lng
    };
    lastMapMeaningfulCenter = center;

    gmapsMap = new gmaps.Map($map, {
      // @ts-ignore
      styles: mapStyle,
      center: center,
      // restriction: {
      //   latLngBounds: GMAP_USA_BOUNDS,
      //   strictBounds: false,
      // },
      zoom: zoom,
      disableDefaultUI: true
    });

    gmapsPlaces = new gmaps.places.PlacesService(gmapsMap);

    gmapsCircle = new gmaps.Circle({
      center: center,
      radius: getDistanceInMeters(),
      visible: false,
      map: gmapsMap,
      fillOpacity: 0.05,
      strokeOpacity: 0.2,
      strokeWeight: 1
    });
  }

  /**
   * Listen Google Maps events
   *
   * Debounced listeners on bounds changed and scroll to top on dragend,
   * other useful events are `dblclick`, `drag`, `zoom_changed`
   */
  function listenGmapsEvents() {
    gmapsMap.addListener("bounds_changed", debounce(handleBoundsChanged, 100));
    gmapsMap.addListener("idle", handleNavigationUrl);
    gmapsMap.addListener("dragend", () => scrollResultsTo());
    gmapsMap.addListener("zoom_changed", () => {
      handleNavigationUrl();
      if (!canStartRenderResults && gmapsMap.getZoom() >= 6) {
        canStartRenderResults = true;
      }
      scrollResultsTo();
    });
  }

  /**
   * Update URL deeplink, without pushing a history state
   */
  function handleNavigationUrl() {
    const zoom = gmapsMap.getZoom();
    const center = gmapsMap.getCenter();
    const posLat = center.lat();
    const posLng = center.lng();

    navigateToMergedParams({ ...getUrlQueryParams(), posLat, posLng, zoom }, true);
  }

  /**
   * On manual address search
   *
   * called either when the user press enter without selecting an autocompleted
   * option, or in the same scenario if the user clicks on the submit button
   *
   * @see https://developers.google.com/maps/documentation/javascript/reference/places-service#FindPlaceFromQueryRequest
   * @param {string} query
   * @param {Function} [callback]
   */
  function handleFreeQuerySearch(query, callback) {
    if (!gmaps) return;

    loadingStart();

    gmapsPlaces.findPlaceFromQuery(
      {
        query,
        fields: ["geometry", "formatted_address"],
        // the bias used when searching for Place. The result will be biased
        // towards, but not restricted to, the given LocationBias
        locationBias: GMAP_USA_BOUNDS
      },
      (results, status) => {
        if (status === gmaps.places.PlacesServiceStatus.OK) {
          const firstResult = results[0];
          if (firstResult && firstResult.geometry) {
            const lat = firstResult.geometry.location.lat();
            const lng = firstResult.geometry.location.lng();
            const bounds = handleFoundAddress({ lat, lng });
            const address = firstResult.formatted_address;
            centerMapOnAddressBounds(bounds);

            if (address) {
              searchaddressInstance.$input.value = address;
              navigateToMergedParams({
                ...getUrlQueryParams(),
                address,
                query: undefined
              });
            }
          }
        }

        if (callback) callback();
        loadingDone();
      }
    );
  }

  /**
   * Handle `bounds_changed` Google Maps event, set the currently visible and
   * results places based on the current map bounds.
   *
   * If a search address is found and active visible places will always begin
   * with those. Otherwise, in the case of a free navigation interaction
   * (without a search address) we start from an empty list and populate the
   * results view with just those currently visible on the map. We then get the
   * center and the bounds of the map, then check the places that are visible
   * within the current bounds and compute the distance of each place to the
   * current center of the map. Then all visible places are sorted by distance
   * and rendered, in case we have started with the results places (if there is
   * an active address search) we make sure there are no duplicated results.
   * N.B.: to avoid a too fast rendering we debounce all this.
   */
  function handleBoundsChanged() {
    // bail if places have not been yet created
    if (!placesList) return;
    // console.log("handleBoundsChanged");
    const bounds = gmapsMap.getBounds();

    if (hasSearchedAddress) {
      placesVisible = placesResults;
    } else {
      placesVisible = [];
      setFreeMapMeaningfulCenter();
      // calculate and display current map distance range
      const from = gmapsMap.getCenter();
      const to = bounds.getNorthEast();
      const currentDistance = computeDistance(
        {
          lat: from.lat(),
          lng: from.lng()
        },
        {
          lat: to.lat(),
          lng: to.lng()
        }
      );

      $distanceCustom.textContent = " " + Math.round(currentDistance) + " ";
      setDistanceStatus(false, "custom");
    }

    updateVisiblePlaces(bounds);
    if (canStartRenderResults) syncResults(placesVisible);
    syncLastInfoWindow();

    forEach($all("storeresult:phone"), el => {
      on(el, 'click', () => {
        const title = el.getAttribute('class')
        const split = title.split(':')
        const finalTitle = split[split.length - 1]
        const action = el.getAttribute('title')
        dataLayerStore('distributor info click', action, finalTitle)
      })
    });
  }

  /**
   * Handle distance select click action, it displays a warning if the select
   * is disabled, that is the case for when an address is not entered and found
   * (either manual address or found place)
   */
  function handleClickDistance(event) {
    if ($distance.disabled) {
      event.preventDefault();
      setDistanceStatus(false, "warning");
      searchaddressInstance.$input.focus();
    }
  }

  /**
   * Handle distance select change action
   */
  function handleChangeDistance() {
    const { lat, lng } = lastMapMeaningfulCenter;
    const bounds = getBoundsBasedOnDistance(lat, lng);
    navigateToMergedParams({ ...getUrlQueryParams(), distance: $distance.value }, true);
    centerMapOnAddressBounds(bounds);
  }

  /**
   * On found address (both by selecting an autocompleted result or from a free
   * text search result)
   *
   * @param {google.maps.LatLngLiteral} latLng
   * @returns {google.maps.LatLngBounds}
   */
  function handleFoundAddress({ lat, lng }) {
    const bounds = getBoundsBasedOnDistance(lat, lng);
    canStartRenderResults = true;
    hasSearchedAddress = true;
    lastMapMeaningfulCenter = { lat, lng };
    gmapsCircle.setVisible(true);
    setDistanceStatus(true);
    navigateToMergedParams({ ...getUrlQueryParams(), lat, lng });
    handleNavigationUrl();
    updateResultsPlaces(bounds);
    return bounds;
  }

  /**
   * Handle marker click
   *
   * The problematic scenario here happens when the user clicks from marker to
   * marker on map therefore the map pans and change its bounds, so we need to
   * refresh the visible list and then scroll to the place just clicked.
   * So we bind once the scroll action and always trigger a `bounds_changed`
   * event in order to refresh the visible results list.
   * It would be probably enough to have a gmapsMap.panTo callback but it does not
   * exists.
   *
   * For similar issues see:
   * @see https://stackoverflow.com/questions/6304208/google-maps-v3-panto-callback
   *
   * @param {Locator_Place} place
   */
  function handleMarkerClick(place) {
    canStartRenderResults = true;

    openInfoWindow(place);

    dataLayerStore('distributor location click', place.name)

    loadingStart();

    setTimeout(() => {
      scrollResultsTo(place);
      if (__DEV__)
        console.log(
          "handleMarkerClick: scroll to place after possible but undetectable panning"
        );
      loadingDone();
    }, 400);
  }

  /**
   * Set distance select status
   *
   * @param {boolean} enabled
   * @param {"warning" | "custom"} [status]
   */
  function setDistanceStatus(enabled, status) {
    setDataAttr($distanceWrap, "status", status);
    setDataAttr($distanceWrap, "is", enabled ? "enabled" : "disabled");
    $distance.disabled = !enabled;
  }

  /**
   * Center map on address bounds, this happen in three scenarios:
   * - user has found a place via selecting ad Autocomplete result
   * - user has found a place via free search PlacesService
   * - user has changed the distance range
   *
   * Here are two attempts to make the map fit closer to the circular
   * highlighted area:
   * ```
   * gmapsMap.fitBounds(bounds, { top: -72, bottom: -72, left: 119, right: 119 });
   * ```
   * or
   * `gmapsMap.setZoom(gmapsMap.getZoom() + 0.5);`
   *
   * @param {google.maps.LatLngBounds} bounds
   */
  function centerMapOnAddressBounds(bounds) {
    gmapsMap.fitBounds(bounds);
    gmapsInfoWindow.close();
  }

  /**
   * Update results data according to the given bounds
   *
   * @param {google.maps.LatLngBounds} bounds
   */
  function updateResultsPlaces(bounds) {
    placesResults = [];

    for (var i = 0; i < placesList.length; i++) {
      const place = placesList[i];
      if (bounds.contains({ lat: place.lat, lng: place.lng })) {
        placesResults.push(place);
      }
    }
  }

  /**
   * Update visible places list and sync results UI
   *
   * @param {google.maps.LatLngBounds} bounds
   */
  function updateVisiblePlaces(bounds) {
    for (var i = 0; i < placesList.length; i++) {
      const place = placesList[i];
      if (bounds.contains({ lat: place.lat, lng: place.lng })) {
        placesVisible.push(place);
      }

      place._distance = computeDistance(place, lastMapMeaningfulCenter);
      place.distance = Math.round(place._distance);
    }

    placesVisible = removeDuplicatesByKey(placesVisible, "uuid");
    placesVisible = sortByDistance(placesVisible);
  }

  /**
   * Set last map meaningful center to the current map center
   */
  function setFreeMapMeaningfulCenter() {
    const currentCenter = gmapsMap.getCenter();
    lastMapMeaningfulCenter = {
      lat: currentCenter.lat(),
      lng: currentCenter.lng()
    };
  }

  /**
   * Get bounds based on distance select value
   *
   * @param {number} lat Center lat
   * @param {number} lng Center lng
   * @returns {google.maps.LatLngBounds}
   */
  function getBoundsBasedOnDistance(lat, lng) {
    gmapsCircle.setCenter({ lat, lng });
    gmapsCircle.setRadius(getDistanceInMeters());
    return gmapsCircle.getBounds();
  }

  /**
   * Create pins for each place on the map
   *
   * It creates a single InfoWindow object to reuse for all markers, this is
   * to achieve one InfoWindow opened at a time (@see https://stackoverflow.com/a/3722454/1938970)
   *
   * @see https://www.svgminify.com/
   * @see https://yoksel.github.io/url-encoder/
   * @param {$response_Place[]} data
   */
  function createMapPlaces(data) {
    const iconDistributor = {
      // small acusa logo, all other values
      // -----------red
      url:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAkCAYAAADhAJiYAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyNJREFUeNq0mD9oFEEUxmc3gQtc9MTASQ5yhcUG8YTgn0YRcp2C19hErWLhWaaIKcWIpaTQ8hpTBdLYKGgTEpBYJRIwQXKCRQInBiJecoEcXOF8k3nr3jq7Ozu792DZvdm9eb/53ps3s2uxFOxT/vIbfhrjR/nm3pc/SfqyUoKZlD83kkJZKcKwNKCslGESQ1k9gEkEZfUIxhjK6iGMEZTVY5jYUFYSmP7cKZa96LCBkQI73m2wzsGhaG/v/mSd5qERlG0Ck7t+hQ3dHmeF6gNWnHks2gDlvHouzhfm5wQsDo+hcC7z/s4YA6lg4MR5PSuuoU7/6ROnxZkqa9QW2PkX06z5eV1AATgulB0X5tLbGvu1+F44a/MwZUuOCFeGK9Pa2hbn9k5DqAgDmM9Coew4OQMYWHN1TTjsHLTccAlgrhZBQqWhW+Oi7dxERRvK1oVBrsBRfeqZCBkcB1m2NCqA8Pz+xxWWv1dRPaaEsqNgMHqoUXxSZXuL73gyl0VYdl7W+MjvuIpQSDNSrYGRYanaoPg/Dl8+KaHsKGXO8tmUu3FVhmqdFR7dd6c0nIvQ8GcoZLCjzW03hyisUA0hjFLKjip6gyVHjBLWJ6cyIJDUsGNecwC2/2FFwBMEKUUGyEyxEBRlF8qOqsDoGKOjkIjOt+r/wiRh0UZKoRzAAEmqQGVSLQzKlhehRvlAjshOEne0qw05RY694NRH1BIDoLIs60pryxpDFTqoc7qHZ0lJryKUbyE2z5eVh7ZcWwKhWpv1rsLodeR1SAlNhsT33sc1CmYYjJvUYVC/eR6o8soPQqHxw3sNNSkMpmvaB0FhWUD9CS6Cjtb+A30odgBdMMrth6wHy95kR3EcW1pQjlrHALJ2reIH+g9GuXSolIJK36dmjWG+3q1qwQQurioo1JRvk9Mq2SNhjjwTIwwmcscYFD7sffITlcic+fF0TlsZ7S2sCorAsFSgJlFxxPIANTCbdBI4yZ5aCRXDtGDivnWYQmnDmLyXxYWKBWP65qoLFRsmybt9FJQRTNKvH0FQxjBpfB/yQyWCSesLGkFtJIWB/RVgAMawoTU41bcZAAAAAElFTkSuQmCC",
      size: new gmaps.Size(36, 36)
      // ----------grey
      // url:
      //   "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAkCAMAAADW3miqAAAATlBMVEVHcExKSkhLS0dJSUhKSkdJSUlJSUhPT09KSkr///+lpaTo6OjS0tL09PTd3d2ZmZmOjo3Gxsa7u7uwsK9VVVSDg4JhYWB3d3dsbGtKSklAB6ceAAAACXRSTlMAr0DvYJ/PEDAp6ceQAAABHElEQVR4XoXQ227CMBAE0FBaYHbXd+f2/z/aGsKIkMrMw2okH60tD4fcTtfhU27n9XL9bFaqjqHqmqb6ZstP11B1DVXH1GWaqP43S8iz2Jgq1dGEUSW6v2GxUr2ZWaNLYqMGLcZde7O40ScvJskXzZHq1UyuBAQIRDCiKF//ajIWmDRmmJExT09FUwNSQXROEJ3KfeG2i2adBeIRkbUNT9QU/1kMAp/ayChoyPPvT1sTRYYZCorCHm3dMlwvjxIcFN4gyIBDAkw38z081YwWjwRrRQEkGiqHewyt3EegocrA83RrlYaqOuwjNC9q3BtPs1NxZyrNXi16vOuo1piaszDRHBVD01M0fUVzVH1DRdNVNH11/mqtr863d/ALR/guTx2SEnAAAAAASUVORK5CYII=",
      // size: new gmaps.Size(36, 36),
    };
    const iconDealer = {
      // small circle icon, value 1
      url:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABYAAAAWCAMAAADzapwJAAAALVBMVEVHcExKSkpJSUlKSklKSklHR0dJSUlJSUlJSUhJSUlLS0dLS0hKSkdKSkhPT0+hNeMzAAAAD3RSTlMAMJ/f/yC/UO+PQHBgrxBT5OTRAAAAjElEQVR42nWRUQ7EIAhEQUeqtPb+x+1sJXV/mESjE3gKyJKWCqoWla1m+GRNQkdn3HB1H8zpR7g8nhI6GfL6raP/EZXXH8eWe02zeS3fuAMk3BYP3uQAKgX1TQkZLxWFa5CAT+QMRgIuMrc9RRygrYuxKZraCSR5MvlgUk5SfNKqrLF7DK4xhnxoyYgfiIYGm4Ko/2wAAAAASUVORK5CYII=",
      size: new gmaps.Size(22, 22)
      // url:
      //   "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='#4A4A49' d='m11 0c-6.0557 0-11 4.9443-11 11s4.9443 11 11 11c6.0557 0 11-4.9443 11-11s-4.9443-11-11-11zm0 20.004c-4.967 0-9.0041-4.0371-9.0041-9.0041s4.0371-9.0041 9.0041-9.0041c4.967 0 9.0041 4.0371 9.0041 9.0041s-4.0371 9.0041-9.0041 9.0041z'/%3E%3Ccircle cx='11' cy='11' r='5.7155' clip-rule='evenodd' fill-rule='evenodd'/%3E%3C/svg%3E",
      // size: new gmaps.Size(22, 22),
      // "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9,0C4,0,0,4,0,9s4,9,9,9s9-4,9-9S14,0,9,0z M9,16.4c-4.1,0-7.4-3.3-7.4-7.4S4.9,1.6,9,1.6s7.4,3.3,7.4,7.4 S13.1,16.4,9,16.4z'/%3E%3Ccircle fill-rule='evenodd' clip-rule='evenodd' cx='9' cy='9' r='4.7'/%3E%3C/svg%3E",
      // size: new gmaps.Size(18, 18),
    };

    const iconCertified = {
      // certified dealer logo, value 2
      url:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACUAAAAlCAMAAADyQNAxAAAAYFBMVEVHcExrdn1KU1dweX5ARUgVFRYlKCmMmaBBSEsRERNSXWN1hIyIlJk6P0B8h4x2g4pdZGhkb3QnLCyMmaB5ipJ8jJSGlp6UoaeBkZl9jZWapqyQnKO7wsezu8Cps7iirLLJ4EfpAAAAFHRSTlMAzI+ZXx0//nEQpu/uTtDfn7cs28cCP9oAAAHHSURBVHhejZPrjpswFIQLS4AECJt0zs033v8tKyvLYrOp2vmB7KPPx8ej4VelS3drJqP1MT7Lcs20q6oamalOt/49NE6sAGUQMNb2+ga6sYJ8iiGEmMRg3FzeQAYfwybM7LcYHBGvZ6xlwxacAUQEkIRoxE196cKGFBhkX4KljLUldF0VLijsEFFMZFo6MjJxGGClyIID3wqqUaQEqwUJZtPxgCeMA5OdRFGg3TfVKVyEnYUtwT6+qQ9F2n5S5KPR/aAM0b2hOCh+V5T8D5X+3WtU5EHPgkQq5lpygX5SKUGPN14n0+DPzXKNbCljg+1sGGGLpFORip7Nwml+cBhQh+LBGEJlBjQ46FTl8DkpJDjsHMGHDcbd6ddgwxCiN2QRpzxAzk2lT28G20JMzrkthsQw8p81dJ1lMCKY31JMSRRkxjLXue+c914z9xIZ6eC9q+d6ZMoPaqTMrGTKueAeJXQZ/K6mvbWN3/dD6cTodsj12eRjOxZUI1+QvCKwfu+bwtNBxInsZ/fezokMzyOFMj/GZRUv82uOy5zXXdvM7kjO0mdfevFut/HTvVy4LPlbqXXS7yGRfOC97vdjuV7/RvVH+3EpgT82XUlpauf7agAAAABJRU5ErkJggg==",
      size: new gmaps.Size(37, 37)
      // "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB8AAAAfCAMAAAAocOYLAAAAYFBMVEVHcEx4hYtue4FDSk4mKixQW18REhKLmaB2ho4EBAR6e3tocHQ5PkGan6Fdam9se4MgIiKzvMGqsraNkpR9jZV5ipKDkpuapq2Glp57jJSwub6QnaOjrrSVoqiSn6a3v8Psnh9PAAAAFHRSTlMA49R/SqQd/vwKGbNlVsP4M5J9RZneGnMAAAFWSURBVHhehZPXcoMwEEVDEaIZO8k29f//y4CNLHA84/vArDh3li3i66BW1T1grebh643aBQgQEYjq+R8dFBFSZO+DIMjSveBFkHyynnl9apT6bFCCMXnCVQZC8qvhWMQsGJKs6C4k61FUwV0FOhFClgHLSO2TN2JsQCgykohKgoWcNXAUem+u447Hq/GMJ260hf6y898erDZwlqX+Z+eXldMLN1b335/4p/yf6iv9RYtv+yvziaf50Gk+Xf8yX5PnW/bDSbIB4bmfUgGGxISbtv0aOl+AWgDFJ8vM3lqHRuojvkVHYJCC954FEcjF24E37JzeHACwUhDnuCl4qIJbpQWqqgLS2yFU5YK1wd3F0zAMEz8OofSn2MU7bzfzzlllPPbM+pEyfyxG5n7M+63V3M2cS2q26DZPVd7Pwzexfoyk0zxtYNjeF3Uy7dHkurc/aJuDuYA/02YvJ9xixdQAAAAASUVORK5CYII=",
      // size: new gmaps.Size(31, 31),
    };

    const iconPremium = {
      // premium dealer logo, value 3
      url:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACUAAAAlCAMAAADyQNAxAAABelBMVEVHcEzR0dEfGhZEPjYhGBJfQjNYSj0GBgYTEw8tIBhHNCd9VUNeQi6KYkKTeGEzIxx0YVNELiJ8WDk1JBsoISGcfmaidE5aTUV+bF+xmYSffWN/WENyUTaKfG6umofJp4aXa0qJXUuldUyygFR9YlVkUEDBnXuDaFTJs52FWUbHpobQuqPDpYq1o42wj29nT0WkiHbAo5C4j2iuiGejc1PVuqSod1OYaVHUtZa4imGldFOhcFKSZFG2hFqzgljgybGbbFLQtJ68mYbRsJCRY1SQYlC8kWmselake2uwflTjzLPPrY7exq+ufFO/noqedWSVZlGwi3mZalKth3XFnnrlz7eeblLMsJq8iFuse1PCmnSmfm23lIHcwKPs1LvZvaDAlXDCo46fblXJpIDewqaheGjXuZuaalibbl3FppLcw62TZljexKmVaVqNX1DLp4W0kH7OsZyrhXO/kmrIq5abcWHOqYaMXEvlyavDoYauhHK8m4iqf2bPr5k7e2/rAAAANHRSTlMAAhs1LpthBhFCatqJw5pNdnK3WiLN6k+IzarRqHml6d3v+PqzgN+i4+nY6M3RxJbZ7/fRwPviMgAAAopJREFUeF5101VXG0EUAOAEiAtuBYoWqNu6W9zd3XD3tv+9ExI2OyTcp91zvnNl5o4OCvOic3l9fff3nnFM91aMLe0/NNvyeTJ/TS4YJkYj4365nAqWiMAzy27bTcPGtPdPRI+Ccp50N/LXbjfJZh1jQ2hHkmpiJSC32wRxlyc5kuSyG6+Z4ekqluFFlE+lTiqEfF7lWMAccNHZg9uWJNZqGTSY8gMGemM5wOzQCXxUFK+UiEkQA9m2tZNO112uVqR1dQEx0Ft2VdO6rR5VFOXWC7Mq5ya3BgOMp+k44zo7fmaHA0a63VmjqqYwKh2PDjE5785fO1XlDGEUPYKdV5PVb6p6F8rlRmWTk43krl5VOZ/PgwzYnz5r3wUan1TlpEAyDGKXXdYkiMCXQfc0RmGhAUv0GV+pEO9VtYgjNO3LjWDN0prmguo0nhtmR6DouKr0hnoaF0Kv2Y3Il79qtmIiigsM7YNYUSpm0LIRWq8OjjOIlhWuWheH6DK0YGYbg0ddUDYlUgz/fPVELAcukE14GYERhE4hIX3QwTGFnt7jAh5HPOCu0lSOZk5vvdUZGJl+oKj3PpqmESqE4DQiMGf3kcfGrzm4YCws8jVQlcZ6B4LHY8ET+c4KKYM3ERZTwce/COXhWB+VjlxkjvwlYh4acVOJxMKXfKokz1smLauI67RQvAEsoB1ylnJ1mcj7H4zdQ8biffagnXLGI/SY/7O5++/w4D1WXtaraG6L81DxY8CaS8+5rSwC2FP4MsgPbnuJWnDYZ21d1uvDvOBDhE3r9/nDsrpf+kVLt44lqhR2XmZmQx5rt4pxTa+Dw97pTPbVOMKu6kaHacWmrsDKBkg/OizT6qd1Ugv+Axtq72B58j9JAAAAAElFTkSuQmCC",
      size: new gmaps.Size(37, 37)
      // "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB8AAAAfCAMAAAAocOYLAAABelBMVEVHcEwzJBt9eXk9KCExMTF4Vz6WbUgTCgoLCQYcFhIAAAA5LSGFf3x9VD6MeWluSjqomY6OYUgdEw1CLyBQPDCfcE13XkniyrG3pZijmpJoRjjFsqBwUDXVv6pnSDgAAAB7a1zFqY/Bua+Vj4jKtJyJW0BDKBvZwqqldFStk3nFrJm+poxqXFJeVEeEXkFQNy1tSiu5mnu7nIlVOCGUd2eFalyohGO/mXWJeWi0nIPJpYOsmohHPjqZZ0WicVSRYlCtfFXjzbWcbVK1hlyuiXfSsZGVZlHZwKnNqomnd1Ogb1KaalHJpIHFn3uYbFzEm3WdcWKXZ1WkdFPXvKTexKixf1Sbb1+yjnvQtJ6XaFGOYFDAoIypeVOVaVq8h1rm0Li4hFjFpZDGp5O6lIG9kWnbv6Lw2b+6jmapg3LBl3Hmy7DUtZfLrZfDo4/Xup3awqufdWXIqpW5jGO+jGC1j3e+nYrAlGymckjUuaOUZ1mkfWyogXC8moeziGhmYzEqAAAAPnRSTlMAUhBuArDUGw8qBEUc263HTe45YHX1l/5eRax0n4uhCJnoYCn0+YT49c7r7H9nxouv5/eQxrvO6o3H8Lw9/qzFgnAAAAH3SURBVHhebZL1expBEEAhBEiOIEFCk6ZtUnV3yZ66C+5uca/+770DjtuEzm/7vTfzzc6MB4rvn79sf/30cS2EeP4ToW/pUXeXk9ULMpCYo3e2zDSe3pVlSR12Tmv+4FW89KHZLJg0J0mc2idPyFrgirDop6hWpmDW65kDmrsgSUtYgrAvp+X39psFFsdtQbaFTZcHV1ElP6D+tGChEZ/xhwCt5rTfeVdQyf7p61n5MGDQ0s8jWOicdFIrU35vxyCK1wSpPyRjUx675GHhcCyocufmlN+4xLA5QZKGt2Y8i/HgmkDTksPXdyr8nNA94Jz6Kw8MoTIVqo6QztDrzv+igBCyM2Ew+UX92YIzAB8gGB4WzlvHh70td3thoshUYIEq/3i07C7AyxQZWFB+nReewwu+rVuCMRYYVBeVQfv9Iswj7RIjFgXempPAG0xVOwsg0P6Db9myjhLAwHgCCEwJbXNqCEpP5ssse0aIQrbRqADi6Ljepdeg/KhGlc26Kf/1+/0pUaGaLN594x5YnCjlLAEfPbFbBSWF2mfxjHfG32FF1BZ6LywewsSx0Is4eGG1homW0Hxql0QC48fe6L4z31h4M7Hs03PtSUvJBg9exZOR7btOPmK16NnQtclEg6nGS8Seun1/8JFHp/7GY0ucj4TTsNfngn/JAaxt83fkDgAAAABJRU5ErkJggg==",
      // size: new gmaps.Size(31, 31),
    };

    const iconCompany = {
      // large acusa logo, value 4
      url:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEgAAABICAMAAABiM0N1AAAATlBMVEVHcEzDFTHCFTHDFjLBFTLDFjG/Dy/CFjLEFS/////DFjLhi5nwxcz78PLHJT/pqLL44uXLM0zlmaXSUGXabX/str/OQljdfIzWX3L009l1g0ZzAAAACXRSTlMAr4/vYJ8QzzCTyYMvAAACu0lEQVR42qXW227bQAwE0KRt2s5w71rd/v9Hi0FsRzENy+gOIFqytUfkQg9+O8/v979vw5Hzhz8v0qjDizTuUNK446QRR9Kg46RRR9KQMy7JcdKAMyrJGZCcMyrJGZCcMyrJGZe8U730H86W2NJm5JKc9LIzsdcpc2tsIdV9cdKLTsq0NaESSyqBPTrpxbmacc6EMczs+ohL7U46dzpqQiACZ9QUUSeUZE46dZaoPuYJhYaVGZkBLcXFSc+dGlFZYBtAg0ykDtRckpOeOjtMiyeDoMIJ6AnIC4xf0qmTdgSmiMgAMAMk0BgQWVAP0omjFZUNsAQEYYKCvpkaGg/Sc2cFTMNEbUtmBCiRBm1apJceO5oppQJ0NbMuAMQKQp6AzUkPnZW6X6WwAqirDBVmFXXbveT7aSzApmJauXPXPs/Cd0E6muvJO5NplKRS1dCqsiWdqcwEIlt9KH3wELMViJ+TNZEZADuARcUmXdrGO8k5zGZqftbD42fRpfCg3qTRjHeScxgsXN4dzYhVHZiGygtuDbaZTpLjoKI+TA+fTEVnXcTMIigEOon3UJh169VI87WNCdI2VcZ7iD98R9G0Sh3pRDNGFhkQGQCEinvo48EeIWtrwgEKCcA+q1Rp2dCc46SGXSsCkG+QpgoiLKuuBeYcJ3VEAJiBIqgKMijX8dTy5BwnVUCJl2XbN8iuvyXneKngmqZDmx3wmaKpldk7XrIbVMplvqtdbiPyll8XwUsJ32Lwic55KDUcE+BjznkopYhjonOKdx5LG55ncY6X/HA+68E5k/ITJx+cAck5Z5LhcbpzzqSpwGf3+3wu0eIdUzqd8yTvvKXPX0qcN3rn9X+102pK10zOOZd8nDMuyRmQnDMqyRmQnDMqyRmQhhxJI46Xxh1J3hmX5IxK444k5wxJcsalqzMuveT8Az3EbtqSC0e/AAAAAElFTkSuQmCC",
      size: new gmaps.Size(96, 96)
      // "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgBAMAAAAQtmoLAAAAMFBMVEVHcEzDFjHCFjK/Dy/EFS/BFTLDFjL////RSl/vwcnghpXJK0X66u3oo63ZZ3r009laQQ/1AAAABnRSTlMAn9AQMGCVdvoSAAADnUlEQVR4XpXPvY7jZBSH8SC4AKq5ASRqxEVQcwusxBl/xJ5uHtuxnc6BWS10yVLslhl2Re0sS7FdPNL0tkBIdIkWiXaMEKyQrVF0rKPRq32KKMX7189n9kAffDJ7v84efeYeaEDk/YgzEUM4AEO4AEM4AEO4AU24AUO4AU24AUu4AU24AUu4AU24AUu4AUu4AUu4AUtYwBAuwBIOwBIuwBJu4Bdp40oRLqD6Wf4K38mTWhMPA56082perdrgb008DDyRG3/tr6NM/q0sYQF5F5b+IijD5rC6CStDGCBYxJmfeal0t1FePzWEAY6tn/lFSL0qQtbf11PCAl5T7bKLVNjOqbvSX08JC7wppCvPU2Ed0a5yLxNNWCDsb0MWK4TLmP2cQ19NCAtwiNgfc2HhkUTcXa8nhAUKWbHt0pAkpIxJdqUowgARt9JTN6lHImQe2cVSToQF5JoqoPAogmGQCmlEdSIsEDaprLiNKCMSbxjkMe2JsEBEIj3bOYuL+wEee1HEFDjs2Mfk9ZH9jsuANITHJLEmNBBXHds5hWxor9n7ZB5UZEGtCA1EdUO1YjE862nnZAFUFN5WEQqQuQfSceeTh3A4kvhQkYZrdYUC5NuApTS0K7II6s1wCR65XMqJ0IC89cczq47FfPy3PpLHIImcCA3IC5/Ug7rh7kgWw2FDEYCUciI0ID/4pAF4sO1IfHgFpQ9hJoo4ASKb82EwbA4Nl+fkEYxnZKKIMzW4YDkOeMzwVcUO2uEMNXg0+1QJ30JA6oMHbUd2DYeeNFLCV7PPlfACrsZBDNsNST+ekc6V8PHswxMhm2fwktyH4QxIGLlslylgpojrDJ7DOOAbyIbHUHalAmaKOBZQwngJ30EKSQeLJlGAJlZLyOBPeA1XkA+P4R8uFaCJOQ3Lht/ganSA50DHWgGa8AEabuBHeAvAH0DDVgGaiBkr4Sn8BEAHQK0BTTQApA0L2AD3v0sFTImOsZ6yZxzTj6QCpsSO+9IO1V4BUyLgPvSAgwKmRNhwX6OA9ARY4oiN2xNgiQgbBwUYosdUKMASbzC1CrBE2DsAB2EAS0iHjmwKWEK8BtWyMoAhXqtFfjCAIYaFem8BS0j8O2P/VWIAS4y9evnrs5ePRaaA7SOxacD2hdgsYAkLuAkLuAkLuAkLWMICLsICbsICbsICbsICbuLLCeAmvlaAm9CAm7CAm9CAm7CAm9CAmzCAkzCAizCAk3gI+B/blB8nq+SB+AAAAABJRU5ErkJggg==",
      // size: new gmaps.Size(96, 96),
    };

    gmapsInfoWindow = new gmaps.InfoWindow({
      // disableAutoPan: true,
      maxWidth: 286
    });

    gmaps.event.addListener(gmapsInfoWindow, "domready", customizeInfoWindow);

    for (var i = 0; i < data.length; ++i) {
      /** @type {Locator_Place} */
      // @ts-ignore
      const place = data[i];

      const marker = new gmaps.Marker({
        map: gmapsMap,
        // icon: place.markertype ? iconPremium : iconBase,
        icon:
          place.markertype == "4"
            ? iconCompany
            : place.markertype == "3"
            ? iconPremium
            : place.markertype == "2"
            ? iconCertified
            : place.markertype == "1"
            ? iconDealer
            : iconDistributor,
        position: {
          lat: place.lat,
          lng: place.lng
        }
      });

      // this would be nice to be rendered and cached upfront but since we need
      // to show the distance in the InfoWindow we need to re-render it at each
      // opening action
      // marker.infoWindowContent = renderInfoWindow(place);

      marker.addListener("click", () => {
        lastOpenedPlaceUiid = place.uuid;
        handleMarkerClick(place);
      });

      place.marker = marker;

      placesList.push(place);
      placesMap[place.uuid] = place;
    }
  }

  /**
   * Get selected distance in meters (converts miles to meters)
   *
   * @returns {number}
   */
  function getDistanceInMeters() {
    const val = $distance.value || "0";
    return parseFloat(val) * 1609.344;
  }

  /**
   * Compute distance of two sets of coordinates
   *
   * @param {google.maps.LatLngLiteral} from
   * @param {google.maps.LatLngLiteral} to
   * @returns {number} In miles
   */
  function computeDistance(from, to) {
    const meters = gmaps.geometry.spherical.computeDistanceBetween(
      new gmaps.LatLng({ lat: from.lat, lng: from.lng }),
      new gmaps.LatLng({ lat: to.lat, lng: to.lng })
    );
    return meters * 0.000621371; // meters to miles
  }

  /**
   * Sort places by distance
   *
   * @param {Locator_Place[]} places
   */
  function sortByDistance(places) {
    return places.sort(function(a, b) {
      return a.distance < b.distance ? -1 : a.distance > b.distance ? 1 : 0;
    });
  }

  /**
   * Open Google Maps InfoWindow
   *
   * @param {Locator_Place} place
   */
  function openInfoWindow(place) {
    gmapsInfoWindow.setContent(renderInfoWindow(place));
    gmapsInfoWindow.open(gmapsMap, place.marker);
    const elementsInfo = ['phone', 'email', 'web']

    forEach(elementsInfo, e => {
      console.log('test');
      forEach($all(".storetip:"+e), el => {
        console.log('test inside');
        on(el, 'click', () => {
          const title = el.getAttribute('class')
          const split = title.split(':')
          const finalTitle = split[split.length - 1]
          const action = el.getAttribute('title')
          console.log('el', el);
          dataLayerStore('distributor info click', action, finalTitle)
        })
      });
    });
  }

  /**
   * Sync data and UI the lates InfoWindow opened
   *
   * This is needed because when clicking through the markers inside the map
   * the map pans, that changes maps' bounds and so distances are re-computed,
   * the InfoWindow needs then to update its `distance` value.
   */
  function syncLastInfoWindow() {
    if (lastOpenedPlaceUiid) {
      gmapsInfoWindow.setContent(
        renderInfoWindow(placesMap[lastOpenedPlaceUiid])
      );
    }
  }

  /**
   * Hacky way to customize Google Maps InfoWindow through DOM targeting and
   * applying custom classes
   * @see https://stackoverflow.com/a/14001481/1938970
   */
  function customizeInfoWindow() {
    try {
      const $content = $(".infowindow:content");
      const $wrap = $content.parentNode.parentNode.parentNode;
      $wrap.classList.add("infowindow:wrap");
      $("button", $wrap).classList.add("infowindow:btn");
    } catch (e) {
      console.warn(
        "@acanto Google Maps API InfoWindow DOM structure has changed!"
      );
    }
  }

  /**
   * Scroll to place in results column  (only on screens big enough
   *
   * @param {Locator_Place} [place]
   */
  function scrollResultsTo(place) {
    if (innerWidth < breakpoints.md) {
      return;
    }
    const dynamicSpeed = place ? 500 / placesVisible.length : 0;
    const $placeResult = place ? $(`#locator-result-${place.uuid}`) : null;

    scrollTo($placeResult || 0, {
      container: $resultsScroller,
      speed: 5 /* Math.max(200, dynamicSpeed) */
    });
  }

  /**
   * Check query params deeplinked in URL
   */
  function getDeeplinkedState() {
    const {
      lat,
      lng,
      posLat,
      posLng,
      zoom,
      distance,
      address,
      query
    } = getUrlQueryParams();

    return {
      lat: parseFloat(lat),
      lng: parseFloat(lng),
      posLat: parseFloat(posLat),
      posLng: parseFloat(posLng),
      zoom: parseInt(zoom),
      distance: parseFloat(distance),
      address: address ? decodeURIComponent(address) : "",
      query: query ? decodeURIComponent(query) : ""
    };
  }

  /**
   * Async fetch of all places from the backend
   *
   * @returns {Promise<$response>}}
   */
  function getRemoteData() {
    return ajax(dataEndpoint);
  }

  /**
   * Sync (data and UI) of search results view
   *
   * @param {Locator_Place[]} data
   */
  function syncResults(data) {
    $results.innerHTML = renderResults({
      results: data
    });

    const elements = ['phone', 'email', 'web']

    forEach(elements, e => {
      forEach($all(".storeresult:"+e), el => {
        on(el, 'click', () => {
          const title = el.getAttribute('class')
          const split = title.split(':')
          const finalTitle = split[split.length - 1]
          const action = el.getAttribute('title')
          console.log('el', el);
          dataLayerStore('distributor info click', action, finalTitle)
        })
      });
    });

  }

  /**
   * Handle click on the menu via event delegation, it is faster here to have
   * a single listener.
   * Dont' prevent default events, so links are followed.
   * We let the AutoPan feature of the InfoWindow handle the recentering
   */
  function bindResultsClick() {
    on($results, "click", event => {
      const $place = event.target.closest(`[data-${ATTR_PLACE_UUID}]`);

      if ($place) {
        const placeUiid = getDataAttr($place, ATTR_PLACE_UUID);
        const place = placesMap[placeUiid];
        lastOpenedPlaceUiid = placeUiid;

        dataLayerStore('distributor location click', place.name)

        // if (__DEV__) {
        //   console.log("placeUiid", placeUiid, place);
        // }

        // centerMap(place.lat, place.lng /* , "+2" */);
        openInfoWindow(place);

        // scroll back up to map on small screens where layout is stacked
        if (innerWidth < breakpoints.md) {
          scrollTo($map);
        }
      }
    });
  }

  /**
   * Show loading state
   */
  function loadingStart() {
    $root.classList.add("is-loading");
  }

  /**
   * Hide loading state
   */
  function loadingDone() {
    $root.classList.remove("is-loading");
  }

  /**
   * Center map to given lat and lng, this is used both for centering based on
   * a deeplink and for centering after a search address.
   *
   * @deprecated
   * @param {number} newLat
   * @param {number} newLng
   * @param {string} [zoom] E.g. "7" | "+3" | "-1"
   * @param {number} [max] Maximum zoom value
   * @param {number} [min] Minimum zoom value
   */
  // function centerMap(newLat, newLng, zoom = "10", max = 12, min = 8) {
  //   const newCenter = new gmaps.LatLng(newLat, newLng);

  //   // if the user is navigating the map with an address set calculate
  //   // the new bounds are ased on:
  //   // - the current bounds
  //   // - the searched address
  //   // - the distance area around the searched address
  //   // - the new given lat and lng.
  //   // @see https://stackoverflow.com/a/43519272/1938970
  //   // @see https://stackoverflow.com/a/2205577/1938970
  //   if (hasSearchedAddress) {
  //     // const distance = parseFloat($distance.value);
  //     // const addressCenter = new gmaps.LatLng({
  //     //   lat: lastMapMeaningfulCenter.lat,
  //     //   lng: lastMapMeaningfulCenter.lng
  //     // });
  //     const bounds = gmapsMap.getBounds();
  //     // bounds.extend(addressCenter);
  //     bounds.extend(newCenter);
  //     // if (distance) {
  //     //   const furthestEdgeCoords = gmaps.geometry.spherical.computeOffset(
  //            addressCenter, distance, 90
  //          );
  //     //   bounds.extend(furthestEdgeCoords);
  //     // }
  //     // gmapsMap.fitBounds(bounds);
  //     gmapsMap.panToBounds(bounds);
  //     // gmapsMap.setCenter(bounds.getCenter());
  //   }
  //   // if the user is freely navigating the map without an address set:
  //   else {
  //     let finalZoomVal;
  //     const num = parseFloat(zoom);
  //     const operator = zoom.match(/^[\+\-]/);

  //     if (operator) {
  //       const zoomSign = operator[0];
  //       const currentZoom = gmapsMap.getZoom();
  //       if (zoomSign === "-") {
  //         finalZoomVal = Math.max(min, Math.min(max, currentZoom - num));
  //       } else if (zoomSign === "+") {
  //         finalZoomVal = Math.max(min, Math.min(max, currentZoom + num));
  //       }
  //     } else {
  //       finalZoomVal = num;
  //     }
  //     gmapsMap.setZoom(finalZoomVal);
  //     gmapsMap.panTo(newCenter);
  //   }
  // }

  /**
   * Compute distance of two sets of coordinates (no external API)
   *
   * @see https://stackoverflow.com/a/52211669/1938970
   * @deprecated
   * @param {google.maps.LatLngLiteral} from
   * @param {google.maps.LatLngLiteral} to
   * @returns {number} In miles
   */
  // function computeDistance(from, to) {
  //   const radlat1 = (Math.PI * from.lat) / 180;
  //   const radlat2 = (Math.PI * to.lat) / 180;
  //   const theta = from.lng - from.lng;
  //   const radtheta = (Math.PI * theta) / 180;
  //   let dist =
  //     Math.sin(radlat1) * Math.sin(radlat2) +
  //     Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
  //   dist = Math.acos(dist);
  //   dist = (dist * 180) / Math.PI;
  //   dist = dist * 60 * 1.1515;
  //   // in km
  //   // dist = dist * 1.609344;
  //   // in miles
  //   dist = dist * 0.8684;
  //   return dist;
  // }

  /**
   * Async search places in the backend
   *
   * @param {$request} params
   * @deprecated
   */
  // function asyncSearch({ lat, lng, threshold }) {
  //   const url = `${dataEndpoint}?lat=${lat}&lng=${lng}&threshold=${threshold}`;
  //   const request = ajax(url, {
  //     method: "GET",
  //     url,
  //     data: { lat, lng, threshold }
  //   });
  //   request.then(({ data }) => {
  //     if (data.length) {
  //       syncResults(data);
  //     }
  //   });
  // }

  return {
    init
  };
}

// init
locator(globalConf.cmsApiUrl+"/getstorelocator").init();
